import { getShareholder } from '@services/CooperativeMembershipService'
import { getForestOwner } from '@services/ForestOwners/ForestOwnerService'
import { getProperty } from '@services/PropertyService'
import { CooperativeMembershipLookupError } from '@/errors/cooperativeMembership'
import type { ForestOwnerModel } from '@/types/ForestOwners/ForestOwner'
import type { PropertyDetailsDto } from '@/types/PropertyDto'
import {
  formatPropertyNumberLabel,
  getCooperativeMemberDisplayName,
  hasDisplayContext,
  type WarningCopyParams,
} from './cooperativeMembershipDisplay'

export interface CooperativeMembershipWarningContext extends WarningCopyParams {
  cooperativeMembershipId: string | null
  memberForestOwnerId: string | null
  memberCustomerNumber: string | null
  isGenericWarning: boolean
}

/**
 * Resolves the existing member's customer number ("Kundenummer") for display in the
 * register-shareholder blocking warning. Reuses an already-fetched forest owner from
 * `forestOwnersById` when available; otherwise issues one extra lookup. Returns `null`
 * (never throws) so a missing customer number degrades gracefully rather than failing
 * the whole membership resolution.
 */
async function resolveMemberCustomerNumber(
  memberForestOwnerId: string | null,
  forestOwnersById: Map<string, ForestOwnerModel>,
): Promise<string | null> {
  if (!memberForestOwnerId) return null

  const knownForestOwner = forestOwnersById.get(memberForestOwnerId)
  if (knownForestOwner) {
    return knownForestOwner.customerNumber ?? null
  }

  try {
    const forestOwner = await getForestOwner(memberForestOwnerId)
    return forestOwner.customerNumber ?? null
  } catch {
    return null
  }
}

function collectCooperativeMembershipIds(
  property: PropertyDetailsDto,
  forestOwners: ForestOwnerModel[],
): Set<string> {
  const ids = new Set<string>()

  if (property.cooperativeMembershipId) {
    ids.add(property.cooperativeMembershipId)
  }

  for (const [index, ownership] of property.ownerships.entries()) {
    if (ownership.type === 'UsageRights') {
      continue
    }

    const forestOwner = forestOwners[index]
    if (forestOwner.cooperativeMembershipId) {
      ids.add(forestOwner.cooperativeMembershipId)
    }
  }

  return ids
}

function resolveMemberForestOwnerId(
  property: PropertyDetailsDto,
  cooperativeMembershipId: string,
  membership: Awaited<ReturnType<typeof getShareholder>>,
  forestOwnersById: Map<string, ForestOwnerModel>,
): string | null {
  for (const ownership of property.ownerships) {
    const forestOwner = forestOwnersById.get(ownership.forestOwnerId)
    if (forestOwner?.cooperativeMembershipId === cooperativeMembershipId) {
      return ownership.forestOwnerId
    }
  }

  return membership.forestOwner?.id ?? null
}

// Frontend-only lookup: fan-out via getProperty + getForestOwner + getShareholder.
// Forest owners are fetched in parallel to avoid N sequential requests per ownership.
// Future: replace with exposed GET /properties/{id}/cooperative-membership when BE ships (see spec).
export async function resolveCooperativeMembershipForProperty(
  propertyId: string,
  options?: { propertyNumberOverride?: string },
): Promise<CooperativeMembershipWarningContext | null> {
  let property: PropertyDetailsDto

  try {
    property = await getProperty(propertyId)
  } catch (error) {
    throw new CooperativeMembershipLookupError(
      'Failed to load property for membership lookup',
      error,
    )
  }

  let forestOwners: ForestOwnerModel[]
  try {
    forestOwners = await Promise.all(
      property.ownerships.map((ownership) => getForestOwner(ownership.forestOwnerId)),
    )
  } catch (error) {
    throw new CooperativeMembershipLookupError(
      'Failed to resolve forest owner cooperative membership',
      error,
    )
  }

  const forestOwnersById = new Map(
    property.ownerships.map((ownership, i) => [ownership.forestOwnerId, forestOwners[i]]),
  )

  const membershipIds = collectCooperativeMembershipIds(property, forestOwners)

  if (membershipIds.size === 0) {
    return null
  }

  if (membershipIds.size > 1) {
    const propertyNumber =
      formatPropertyNumberLabel(property, options?.propertyNumberOverride) ?? ''
    return {
      cooperativeMembershipId: null,
      memberForestOwnerId: null,
      memberCustomerNumber: null,
      propertyNumber,
      memberName: '',
      isGenericWarning: true,
    }
  }

  const cooperativeMembershipId = [...membershipIds][0]

  let membership: Awaited<ReturnType<typeof getShareholder>>
  try {
    membership = await getShareholder(cooperativeMembershipId)
  } catch (error) {
    throw new CooperativeMembershipLookupError('Failed to load cooperative membership', error)
  }

  const memberName = getCooperativeMemberDisplayName(membership)
  const propertyNumber = formatPropertyNumberLabel(property, options?.propertyNumberOverride)
  const memberForestOwnerId = resolveMemberForestOwnerId(
    property,
    cooperativeMembershipId,
    membership,
    forestOwnersById,
  )
  const displayContext = memberName && propertyNumber ? { propertyNumber, memberName } : null

  if (!hasDisplayContext(displayContext) || !memberForestOwnerId) {
    throw new CooperativeMembershipLookupError(
      'Missing cooperative membership display context for warning modal',
    )
  }

  const memberCustomerNumber = await resolveMemberCustomerNumber(
    memberForestOwnerId,
    forestOwnersById,
  )

  return {
    cooperativeMembershipId,
    propertyNumber: displayContext.propertyNumber,
    memberName: displayContext.memberName,
    memberForestOwnerId,
    memberCustomerNumber,
    isGenericWarning: false,
  }
}
